import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const EntityMappingPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ from_entity: 'client', to_entity: 'loan', from_id: '', to_id: '', relationship_type: 'one_to_one', notes: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/entity-mappings'); setItems(res.data); }
    catch { setItems([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.from_entity || !form.to_entity) { toast.error('Both entities are required'); return; }
    try { await api.post('/entity-mappings', form); toast.success('Mapping created'); setShowModal(false); setForm({ from_entity: 'client', to_entity: 'loan', from_id: '', to_id: '', relationship_type: 'one_to_one', notes: '' }); load(); }
    catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try { await api.delete(`/entity-mappings/${id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const relColor = { one_to_one: 'bg-blue-50 text-blue-700', one_to_many: 'bg-purple-50 text-purple-700', many_to_many: 'bg-amber-50 text-amber-700' };

  return (
    <AdminSubPage title="Entity to Entity Mapping" breadcrumb={['Admin', 'System', 'Entity Mapping']} backPath="/dashboard/admin/system" actionLabel="+ Create Mapping" onAction={() => setShowModal(true)}>
      <div className="p-4 bg-blue-50 border border-blue-200 text-sm text-blue-800 mb-2">
        Entity mappings define relationships between different data entities in the system (e.g., linking a client to a loan officer, or a group to an office).
      </div>
      <div className="bg-white border border-gray-200" data-testid="entity-mapping-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">From Entity</th>
            <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500"></th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">To Entity</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Relationship</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Notes</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            items.length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-gray-400"><GitMerge className="w-8 h-8 mx-auto mb-2 text-gray-300" />No entity mappings defined.</td></tr> :
            items.map(item => (
              <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`em-row-${item.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f] capitalize">{item.from_entity}{item.from_id && <span className="text-xs text-gray-400 ml-1 font-mono">({item.from_id})</span>}</td>
                <td className="px-4 py-3 text-center"><GitMerge className="w-4 h-4 text-[#C5A059] mx-auto" /></td>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f] capitalize">{item.to_entity}{item.to_id && <span className="text-xs text-gray-400 ml-1 font-mono">({item.to_id})</span>}</td>
                <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-medium ${relColor[item.relationship_type] || 'bg-gray-50 text-gray-600'}`}>{(item.relationship_type || '').replace(/_/g, ' ')}</span></td>
                <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">{item.notes || '-'}</td>
                <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent aria-describedby="em-desc"><DialogHeader><DialogTitle>Create Entity Mapping</DialogTitle></DialogHeader>
          <p id="em-desc" className="sr-only">Entity mapping form</p>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>From Entity</Label>
                <Select value={form.from_entity} onValueChange={v => setForm({...form, from_entity: v})}>
                  <SelectTrigger data-testid="em-from-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem><SelectItem value="loan">Loan</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem><SelectItem value="office">Office</SelectItem>
                    <SelectItem value="group">Group</SelectItem><SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>To Entity</Label>
                <Select value={form.to_entity} onValueChange={v => setForm({...form, to_entity: v})}>
                  <SelectTrigger data-testid="em-to-entity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem><SelectItem value="loan">Loan</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem><SelectItem value="office">Office</SelectItem>
                    <SelectItem value="group">Group</SelectItem><SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>From ID (optional)</Label><Input value={form.from_id} onChange={e => setForm({...form, from_id: e.target.value})} placeholder="Specific record ID" /></div>
              <div><Label>To ID (optional)</Label><Input value={form.to_id} onChange={e => setForm({...form, to_id: e.target.value})} placeholder="Specific record ID" /></div>
            </div>
            <div><Label>Relationship Type</Label>
              <Select value={form.relationship_type} onValueChange={v => setForm({...form, relationship_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="one_to_one">One to One</SelectItem>
                  <SelectItem value="one_to_many">One to Many</SelectItem>
                  <SelectItem value="many_to_many">Many to Many</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="em-submit-btn">Create Mapping</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default EntityMappingPage;
